import { useEffect } from 'react';

export type UploadStatusType = 'uploading' | 'success' | 'error';

interface UploadStatusModalProps {
  isOpen: boolean;
  status: UploadStatusType;
  totalCount: number;
  successCount: number;
  errorCount: number;
  onClose: () => void;
  /** The type of item being uploaded (e.g., "photo", "receipt") */
  itemType?: string;
  /** Plural form of itemType (defaults to itemType + 's') */
  itemTypePlural?: string;
}

export function UploadStatusModal({
  isOpen,
  status,
  totalCount,
  successCount,
  errorCount,
  onClose,
  itemType = 'file',
  itemTypePlural,
}: UploadStatusModalProps) {
  const plural = itemTypePlural || `${itemType}s`;

  // Auto-close success modal after 2 seconds
  useEffect(() => {
    if (isOpen && status === 'success') {
      const timer = setTimeout(() => {
        onClose();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, status, onClose]);

  if (!isOpen) return null;

  const getItemLabel = (count: number) => (count === 1 ? itemType : plural);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/50 ${status === 'uploading' ? '' : 'cursor-pointer'}`}
        onClick={status !== 'uploading' ? onClose : undefined}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 overflow-hidden">
        <div className="p-6 flex flex-col items-center text-center">
          {status === 'uploading' && (
            <>
              {/* Spinner */}
              <div className="w-16 h-16 mb-4">
                <svg
                  className="animate-spin w-full h-full text-primary-600"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-secondary-900 mb-1">
                Uploading {totalCount === 1 ? itemType : plural}
              </h3>
              <p className="text-secondary-600">
                Please wait while we upload {totalCount} {getItemLabel(totalCount)}...
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              {/* Success Icon */}
              <div className="w-16 h-16 mb-4 bg-green-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-10 h-10 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-secondary-900 mb-1">
                Upload Complete!
              </h3>
              <p className="text-secondary-600">
                Successfully uploaded {successCount} {getItemLabel(successCount)}.
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              {/* Warning Icon */}
              <div className="w-16 h-16 mb-4 bg-yellow-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-10 h-10 text-yellow-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-secondary-900 mb-1">
                Upload Completed with Errors
              </h3>
              <p className="text-secondary-600 mb-4">
                {successCount} of {totalCount} {getItemLabel(totalCount)} uploaded successfully.
                <br />
                {errorCount} failed to upload.
              </p>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
              >
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}